import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Alert,
  Box,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { FC, memo, useCallback, useMemo } from "react";
import {
  StepRequirement,
  TConfigItemType,
  TOnboardingConfig,
  TVerificationAppElement,
  TVerificationAppFieldConfigItem,
  VerificationAppDocumentType,
} from "../../models";
import { AddFieldButton } from "./AddFieldButton";
import { FieldCard } from "./FieldCard";
import {
  getStepHeading,
  moveElement,
  stepHasDistinctName,
  stepSupportsFields,
} from "./helpers";
import { StepRequirementsEditor } from "./StepRequirementsEditor";
import { StepTypeChip } from "./StepTypeChip";
import { StepTypeSpecificProps } from "./StepTypeSpecificProps";

export const StepCard: FC<{
  config: TOnboardingConfig;
  step: TVerificationAppElement;
  index: number;
  totalSteps: number;
  onChange: (updated: TVerificationAppElement) => void;
  onDelete: () => void;
  onMove: (direction: "up" | "down") => void;
}> = memo(({ config, step, index, totalSteps, onChange, onDelete, onMove }) => {
  const commitStepChange = onChange;

  const handleDeleteClick = useCallback(() => {
    if (window.confirm("Delete this step?")) {
      onDelete();
    }
  }, [onDelete]);

  const setStepStringProp = useCallback(
    (key: keyof TVerificationAppElement, value: string) => {
      commitStepChange({ ...step, [key]: value });
    },
    [commitStepChange, step],
  );

  const fields = useMemo(() => step.fields ?? [], [step.fields]);

  const handleFieldChange = useCallback(
    (fieldIdx: number, updated: TVerificationAppFieldConfigItem) => {
      commitStepChange({
        ...step,
        fields: fields.map((f, i) => (i === fieldIdx ? updated : f)),
      });
    },
    [commitStepChange, step, fields],
  );

  const handleFieldDelete = useCallback(
    (fieldIdx: number) => {
      commitStepChange({
        ...step,
        fields: fields.filter((_, i) => i !== fieldIdx),
      });
    },
    [commitStepChange, step, fields],
  );

  const handleFieldMove = useCallback(
    (fieldIdx: number, direction: "up" | "down") => {
      commitStepChange({
        ...step,
        fields: moveElement(fields, fieldIdx, direction),
      });
    },
    [commitStepChange, step, fields],
  );

  const handleAddField = useCallback(
    (type: TConfigItemType) => {
      commitStepChange({
        ...step,
        fields: [...fields, { type, label: "" }],
      });
    },
    [commitStepChange, step, fields],
  );

  const handleRequirementsChange = useCallback(
    (requirements: StepRequirement[] | undefined) => {
      const updatedStep: TVerificationAppElement = {
        ...step,
        requirements,
      };

      if (!requirements) {
        delete updatedStep.requirements;
      }

      commitStepChange(updatedStep);
    },
    [commitStepChange, step],
  );

  const showCustomNameWarning =
    step.type === VerificationAppDocumentType.Custom && !step.name?.trim();

  return (
    <Paper
      sx={{
        padding: "20px 32px",
        background: "#ffffff",
        borderRadius: "14px",
        border: "none",
      }}
    >
      <Stack spacing={2}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {getStepHeading(step, index)}
          </Typography>
          {stepHasDistinctName(step) && <StepTypeChip type={step.type} />}
          <Box sx={{ flex: 1 }} />
          <IconButton
            aria-label="Move up"
            onClick={() => onMove("up")}
            disabled={index === 0}
            size="small"
          >
            <ArrowUpwardIcon />
          </IconButton>
          <IconButton
            aria-label="Move down"
            onClick={() => onMove("down")}
            disabled={index >= totalSteps - 1}
            size="small"
          >
            <ArrowDownwardIcon />
          </IconButton>
          <IconButton
            aria-label="Delete step"
            onClick={handleDeleteClick}
            size="small"
            sx={{
              color: (theme) => alpha(theme.palette.error.main, 0.45),
              "&:hover": {
                color: "error.main",
                bgcolor: (theme) => alpha(theme.palette.error.main, 0.12),
              },
            }}
          >
            <DeleteIcon />
          </IconButton>
        </Stack>
        <TextField
          label="Name"
          value={step.name ?? ""}
          onChange={(e) => setStepStringProp("name", e.target.value)}
          size="small"
          fullWidth
        />
        <TextField
          label="Title"
          value={step.title ?? ""}
          onChange={(e) => setStepStringProp("title", e.target.value)}
          fullWidth
          size="small"
        />
        <TextField
          label="Subtitle"
          value={step.subtitle ?? ""}
          onChange={(e) => setStepStringProp("subtitle", e.target.value)}
          fullWidth
          multiline
          minRows={2}
          size="small"
        />
        {showCustomNameWarning && (
          <Alert severity="warning">Custom steps must have a name</Alert>
        )}
        <StepTypeSpecificProps step={step} onChange={commitStepChange} />
        {stepSupportsFields(step) && (
          <Stack spacing={0}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              Fields
            </Typography>
            {fields.length === 0 ? (
              <Typography color="text.secondary" variant="body2" sx={{ py: 1 }}>
                No fields. Add a field.
              </Typography>
            ) : (
              fields.map((field, fi) => (
                <FieldCard
                  config={config}
                  key={fi}
                  field={field}
                  fieldIndex={fi}
                  availableConditionFields={fields.slice(0, fi + 1)}
                  index={fi}
                  stepIndex={index}
                  totalFields={fields.length}
                  onChange={(updated) => handleFieldChange(fi, updated)}
                  onDelete={() => handleFieldDelete(fi)}
                  onMove={(dir) => handleFieldMove(fi, dir)}
                />
              ))
            )}
            <AddFieldButton onAdd={handleAddField} />
            <StepRequirementsEditor
              availableFields={fields}
              config={config}
              requirements={step.requirements}
              stepIndex={index}
              onChange={handleRequirementsChange}
            />
          </Stack>
        )}
      </Stack>
    </Paper>
  );
});
